import { configureSessionBridge } from '@/api/client'
import * as authApi from '@/api/endpoints/auth'
import * as organizationsApi from '@/api/endpoints/organizations'
import * as mfaApi from '@/api/endpoints/mfa'
import { ApiError } from '@/api/errors'
import type { AccessTokenClaims, AuthSession, User } from '@/api/types'
import { config } from '@/config'

import { canOpenPanel } from './panel-access'

/**
 * Store della sessione admin.
 *
 * Vive fuori da React (store osservabile con `useSyncExternalStore`) perché il client
 * HTTP deve poter leggere il token e chiedere un refresh senza passare da un hook.
 *
 * ## Dove stanno i token
 *
 * L'**access token sta solo in memoria**: persisterlo aggiunge superficie di attacco
 * senza benefici, dura 15 minuti e si riottiene con un refresh.
 *
 * Il **refresh token** dipende da `VITE_AUTH_REFRESH_TRANSPORT`:
 *
 * - `cookie` — la scelta raccomandata da `docs/08-api-admin.md`: cookie `HttpOnly`
 *   `Secure` `SameSite=Strict`, invisibile a JavaScript e quindi a un XSS. Richiede
 *   supporto del backend, che l'OpenAPI v1 non descrive ancora (`/auth/refresh`
 *   prende e restituisce il token nel corpo).
 * - `body` — default, l'unica forma che il contract v1 supporta oggi. Il token va
 *   conservato dal client: si usa `sessionStorage`, non `localStorage`. Resta
 *   leggibile da un XSS, ma muore con la tab e non è condiviso fra tab. È una
 *   mitigazione parziale e dichiarata, non una soluzione: vedi README.
 */

const REFRESH_STORAGE_KEY = 'gymmi-admin.refresh'

export type SessionState =
  /** Bootstrap in corso: si sta tentando un refresh silenzioso. */
  { status: 'loading' } | { status: 'anonymous' } | { status: 'authenticated'; user: User }

interface Snapshot {
  state: SessionState
  /** Incrementa a ogni cambio, per invalidare le cache che dipendono dalla sessione. */
  revision: number
}

let snapshot: Snapshot = { state: { status: 'loading' }, revision: 0 }
let accessToken: string | null = null
let refreshToken: string | null = null

const listeners = new Set<() => void>()

function emit(state: SessionState): void {
  snapshot = { state, revision: snapshot.revision + 1 }
  for (const listener of listeners) listener()
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getSnapshot(): Snapshot {
  return snapshot
}

// ── persistenza del refresh token ─────────────────────────────────────────────

function readStoredRefresh(): string | null {
  if (config.refreshTransport === 'cookie') return null
  try {
    return sessionStorage.getItem(REFRESH_STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStoredRefresh(token: string | null): void {
  if (config.refreshTransport === 'cookie') return
  try {
    if (token) sessionStorage.setItem(REFRESH_STORAGE_KEY, token)
    else sessionStorage.removeItem(REFRESH_STORAGE_KEY)
  } catch {
    // Storage negato (modalità privata restrittiva): la sessione resta valida per
    // questa pagina, ma non sopravvive a un ricaricamento. Accettabile.
  }
}

// ── claim dell'access token ───────────────────────────────────────────────────

/**
 * Decodifica il payload del JWT **senza verificarne la firma**.
 *
 * La verifica è del server: qui i claim servono solo a decidere cosa mostrare
 * (es. disabilitare il cambio ruolo sul proprio account). Nessuna decisione di
 * sicurezza si appoggia a questa lettura.
 */
export function decodeAccessToken(token: string): AccessTokenClaims | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const claims = JSON.parse(json) as AccessTokenClaims
    return typeof claims.sub === 'string' ? claims : null
  } catch {
    return null
  }
}

export function getAccessToken(): string | null {
  return accessToken
}

/** Id dell'admin collegato, dal claim `sub`. */
export function getCurrentUserId(): string | null {
  const state = snapshot.state
  return state.status === 'authenticated' ? state.user.id : null
}

// ── applicazione di una sessione ──────────────────────────────────────────────

/**
 * Errore di login rifiutato dal pannello, non dal server.
 *
 * Il nome resta `NotAnAdminError` benché la condizione sia cambiata — «non lavora in
 * nessuna palestra e non amministra il prodotto» —: è il tipo che `login.tsx`
 * riconosce, e rinominarlo sarebbe una modifica di superficie in cambio di niente.
 * Il **messaggio** invece è cambiato, perché quello lo legge una persona.
 */
export class NotAnAdminError extends Error {
  constructor() {
    super(
      'Questo account non lavora in nessuna palestra. Se sei iscritto in palestra, i tuoi dati sono nell’app GymX: questo pannello è per chi la gestisce.',
    )
    this.name = 'NotAnAdminError'
  }
}

/**
 * `true` se questa sessione può **aprire il pannello**.
 *
 * La regola sta in `panel-access.ts` ed è provata lì. Qui c'è solo ciò che serve la
 * rete: la verifica costa **una chiamata a `/me`** e solo per chi non è admin. Non è una
 * difesa — quella è il `403` del server, che resta — ma la differenza fra un rifiuto
 * che spiega e un pannello che si apre e poi non funziona.
 */
async function isPanelSession(session: AuthSession): Promise<boolean> {
  if (isAdminSession(session)) return true
  const memberships = await readMemberships(session.accessToken)
  return canOpenPanel({ platformRole: 'user', memberships })
}

/** Le appartenenze, o `undefined` se non si sono potute leggere — che non è «nessuna». */
async function readMemberships(
  bearerToken: string,
): Promise<{ permissions?: string[] }[] | undefined> {
  try {
    return await organizationsApi.listMemberships(undefined, { bearerToken })
  } catch {
    return undefined
  }
}

function applySession(session: AuthSession): void {
  accessToken = session.accessToken
  refreshToken = session.refreshToken ?? null
  writeStoredRefresh(refreshToken)
  emit({ status: 'authenticated', user: session.user })
}

function clearSession(): void {
  accessToken = null
  refreshToken = null
  writeStoredRefresh(null)
  emit({ status: 'anonymous' })
}

/**
 * `true` se la sessione può usare il perimetro `/admin` — catalogo, utenti, import.
 *
 * Il controllo autorevole è del backend, che risponde `403 forbidden` a un token con
 * `role: user`. Questo serve a non far entrare un utente normale in una UI che
 * fallirebbe su ogni chiamata: meglio un messaggio chiaro al login.
 *
 * **Non** è più la condizione per entrare: quella è `isPanelSession`, che ammette anche
 * chi lavora in palestra. Questa resta perché il pannello ha due perimetri, e le pagine
 * del primo vanno mostrate solo a chi le può leggere.
 */
function isAdminSession(session: AuthSession): boolean {
  if (session.user.role === 'admin') return true
  const claims = decodeAccessToken(session.accessToken)
  return claims?.role === 'admin'
}

// ── azioni ────────────────────────────────────────────────────────────────────

/**
 * Esito del primo passaggio del login — OPS-04.
 *
 * `login()` non può più promettere «o applica la sessione o lancia»: con il 2FA
 * attivo esiste un terzo esito legittimo, «password corretta, manca il codice».
 * Un risultato discriminato costringe il chiamante a gestirlo; un `boolean` o un
 * `void` lo lascerebbero cadere nel ramo dell'errore, dove la UI mostrerebbe un
 * fallimento per un login che sta andando bene.
 */
export type LoginResult = { kind: 'authenticated' } | { kind: 'mfa_required'; expiresIn: number }

/**
 * Buono del secondo passaggio.
 *
 * Vive in una variabile di modulo e **non** viene persistito, esattamente come
 * l'access token: vale due minuti, è monouso e vale un login. Che muoia con un
 * ricaricamento della pagina è il comportamento voluto — si ricomincia dalla
 * password, che è ciò che si vuole quando lo stato del browser è incerto.
 */
let mfaToken: string | null = null

export async function login(email: string, password: string): Promise<LoginResult> {
  let session: AuthSession
  try {
    session = await authApi.login({ email, password })
  } catch (error) {
    // `401 mfa_required` non è un errore da mostrare: è metà del flusso. Il
    // buono arriva in `details`, perché il contract non ha altra busta in cui
    // metterlo su una risposta di errore.
    if (error instanceof ApiError && error.code === 'mfa_required') {
      const challenge = error.mfaChallenge
      if (challenge) {
        mfaToken = challenge.mfaToken
        return { kind: 'mfa_required', expiresIn: challenge.expiresIn }
      }
    }
    throw error
  }

  await applyLoginSession(session)
  return { kind: 'authenticated' }
}

/**
 * Secondo passaggio: codice TOTP oppure codice di recupero.
 *
 * Il buono è monouso: se il server lo consuma e poi il pannello rifiuta la
 * sessione (ruolo insufficiente), non si può ritentare — e infatti si torna
 * dall'inizio, azzerando il buono.
 */
export async function verifyMfa(input: { code?: string; recoveryCode?: string }): Promise<void> {
  if (mfaToken === null) {
    throw new Error('Nessuna verifica in corso. Rifai l’accesso con email e password.')
  }

  try {
    const session = await mfaApi.verifyMfa({
      mfaToken,
      ...(input.code ? { code: input.code } : {}),
      ...(input.recoveryCode ? { recoveryCode: input.recoveryCode } : {}),
    })
    await applyLoginSession(session)
    mfaToken = null
  } catch (error) {
    // Un codice sbagliato **non** brucia il buono: il server conta i tentativi e
    // lascia riprovare. Solo un buono ormai inservibile va buttato, e in quel
    // caso il messaggio del server dice già di ricominciare.
    if (error instanceof ApiError && error.code !== 'mfa_invalid_code') mfaToken = null
    throw error
  }
}

/** `true` se c'è una verifica in corso: la usa la pagina di login per non perdere il passo. */
export function hasPendingMfaChallenge(): boolean {
  return mfaToken !== null
}

/** Abbandona la verifica in corso e torna al primo passaggio. */
export function cancelMfaChallenge(): void {
  mfaToken = null
}

async function applyLoginSession(session: AuthSession): Promise<void> {
  if (!(await isPanelSession(session))) {
    // Nessun token conservato: la sessione non viene mai applicata. Il refresh token
    // appena emesso resta però valido lato server per 30 giorni, quindi va revocato.
    //
    // L'access token va passato **esplicitamente**: `/auth/logout` richiede
    // autenticazione, e il client legge il bearer dallo store — che qui è ancora
    // vuoto, perché la sessione non è stata applicata. Senza, la revoca partirebbe
    // senza `Authorization` e fallirebbe con 401 lasciando il token vivo.
    void authApi.logout(session.refreshToken, { accessToken: session.accessToken }).catch(() => {})
    throw new NotAnAdminError()
  }
  applySession(session)
}

// ── accettare un invito (OPS-02) ──────────────────────────────────────────────
//
// ⚠️ Questo flusso esiste perché il login del pannello e il primo invito si
// escludono a vicenda, e la cosa non è ovvia finché non ci si sbatte contro.
//
// `applyLoginSession` respinge chi non lavora in nessuna palestra — è il gate
// aggiunto il 19 agosto, ed è giusto. Ma chi accetta il **primo** invito è
// esattamente quello: un account senza nessuna appartenenza. Se accettasse dopo il
// login non arriverebbe mai al login; se il gate cadesse, tornerebbe il difetto che
// quel gate ha chiuso.
//
// L'ordine che risolve entrambe le cose è uno solo: **autentica, accetta,
// applica**. L'invito è ciò che rende quella sessione una sessione da pannello,
// quindi va speso prima che il gate la guardi.

/** La sessione autenticata ma **non applicata**, in attesa che l'invito la renda valida. */
let pendingInvitationSession: AuthSession | null = null

/** Come [login], ma senza applicare la sessione: la tiene da parte. */
export async function beginInvitationLogin(email: string, password: string): Promise<LoginResult> {
  pendingInvitationSession = null
  let session: AuthSession
  try {
    session = await authApi.login({ email, password })
  } catch (error) {
    if (error instanceof ApiError && error.code === 'mfa_required') {
      const challenge = error.mfaChallenge
      if (challenge) {
        mfaToken = challenge.mfaToken
        return { kind: 'mfa_required', expiresIn: challenge.expiresIn }
      }
    }
    throw error
  }
  pendingInvitationSession = session
  return { kind: 'authenticated' }
}

/**
 * Come [beginInvitationLogin], ma per chi **non ha ancora un account**.
 *
 * È l'altra metà del buco che l'invito aveva: il flusso è «autentica, accetta, applica»,
 * e chi riceve il primo invito della sua vita non ha niente con cui autenticarsi. Senza
 * questo passaggio l'unica via era registrarsi dall'app mobile e poi tornare sul link,
 * cioè scoprire per tentativi una procedura che nessuno ha scritto da nessuna parte.
 *
 * `POST /auth/register` restituisce già una sessione, quindi non serve un login dopo — e
 * non si deve: sarebbero due sessioni per un account appena nato, e la prima resterebbe
 * viva senza che nessuno la revochi. La sessione resta **in attesa** come nell'altro
 * caso, perché un account nuovo non appartiene ancora a nessuna palestra ed è
 * esattamente ciò che il gate respinge.
 *
 * Nessun 2FA da verificare: un account appena creato non ne ha.
 */
export async function beginInvitationRegistration(input: {
  email: string
  password: string
  name: string
}): Promise<void> {
  pendingInvitationSession = null
  mfaToken = null
  pendingInvitationSession = await authApi.register({
    email: input.email,
    password: input.password,
    profile: { name: input.name },
  })
}

/** Il secondo passaggio del login d'invito, quando l'account ha il 2FA. */
export async function completeInvitationMfa(input: {
  code?: string
  recoveryCode?: string
}): Promise<void> {
  if (mfaToken === null) {
    throw new Error('Nessuna verifica in corso. Rifai l’accesso con email e password.')
  }
  try {
    pendingInvitationSession = await mfaApi.verifyMfa({
      mfaToken,
      ...(input.code ? { code: input.code } : {}),
      ...(input.recoveryCode ? { recoveryCode: input.recoveryCode } : {}),
    })
    mfaToken = null
  } catch (error) {
    if (error instanceof ApiError && error.code !== 'mfa_invalid_code') mfaToken = null
    throw error
  }
}

/**
 * Spende il token dell'invito e **poi** applica la sessione.
 *
 * Funziona in due situazioni che la pagina non deve distinguere: con una sessione già
 * aperta (chi lavora già in una palestra ed è invitato in una seconda) usa quella; con
 * una sessione in attesa da [beginInvitationLogin] usa il suo bearer esplicito, perché
 * lo store è ancora vuoto.
 *
 * Se l'invito non è valido la sessione in attesa **non** viene applicata e il suo
 * refresh token viene revocato: lasciarla viva significherebbe aver fatto entrare nel
 * pannello qualcuno che il gate avrebbe respinto.
 */
export async function acceptPendingInvitation(
  token: string,
): Promise<{ organizationId: string; role: string }> {
  const pending = pendingInvitationSession

  try {
    const result = await organizationsApi.acceptInvitation(token, {
      ...(pending ? { bearerToken: pending.accessToken } : {}),
    })
    if (pending !== null) {
      applySession(pending)
      pendingInvitationSession = null
    }
    return result
  } catch (error) {
    if (pending !== null) {
      pendingInvitationSession = null
      void authApi
        .logout(pending.refreshToken, { accessToken: pending.accessToken })
        .catch(() => {})
    }
    throw error
  }
}

/** Butta la sessione in attesa: la pagina dell'invito che si abbandona non la lascia viva. */
export function cancelPendingInvitation(): void {
  const pending = pendingInvitationSession
  pendingInvitationSession = null
  mfaToken = null
  if (pending !== null) {
    void authApi.logout(pending.refreshToken, { accessToken: pending.accessToken }).catch(() => {})
  }
}

export async function logout(): Promise<void> {
  const token = refreshToken
  mfaToken = null
  // Prima la revoca lato server, poi la pulizia locale: al contrario il token
  // resterebbe valido per 30 giorni.
  try {
    await authApi.logout(token ?? undefined)
  } catch {
    // Un logout che fallisce non deve intrappolare l'operatore nel pannello.
  } finally {
    clearSession()
    // La fine della sessione, anche voluta, deve svuotare la cache e portare via
    // dalla pagina autenticata: senza, il pannello resta a schermo con i dati
    // dell'utente che si stava guardando.
    onSessionEndedHook?.({ kind: 'logout' })
  }
}

// ── refresh serializzato ──────────────────────────────────────────────────────

let inFlightRefresh: Promise<string | null> | null = null

/**
 * Ottiene un access token nuovo, **una richiesta per volta**.
 *
 * I refresh concorrenti sono il modo più semplice di farsi disconnettere: il secondo
 * presenta un token già speso e il server invalida l'intera famiglia rispondendo
 * `401 refresh_token_reused` (docs/03-autenticazione.md). Le chiamate che arrivano
 * mentre un refresh è in volo aspettano lo stesso `Promise`.
 */
export function refreshSession(): Promise<string | null> {
  inFlightRefresh ??= runRefresh().finally(() => {
    inFlightRefresh = null
  })
  return inFlightRefresh
}

async function runRefresh(): Promise<string | null> {
  const stored = refreshToken ?? readStoredRefresh()
  if (config.refreshTransport === 'body' && !stored) return null

  try {
    const session = await authApi.refresh(stored ?? undefined)
    if (!(await isPanelSession(session))) {
      // Il ruolo è stato revocato mentre la sessione era aperta — oppure questo è il
      // refresh silenzioso del bootstrap, e la sessione conservata non è di nessuno
      // che lavori qui. I due casi si trattano uguale, ed è giusto: in entrambi la
      // pagina successiva sarebbe una fila di `403`.
      clearSession()
      return null
    }
    applySession(session)
    return session.accessToken
  } catch (error) {
    // Refresh scaduto, revocato o riusato: non c'è modo di recuperare.
    if (error instanceof ApiError) {
      clearSession()
      return null
    }
    // Errore di rete: il refresh token **non** è stato speso e la sessione è ancora
    // buona. Cancellarla qui disconnetterebbe l'operatore per un blip di connessione,
    // quindi l'errore viene propagato e la richiesta originale fallisce come errore
    // di rete, non come sessione persa.
    throw error
  }
}

/**
 * Tenta di ripristinare la sessione all'avvio.
 *
 * Con transport `cookie` si prova sempre: il cookie c'è o non c'è, e solo il server
 * lo sa. Con transport `body` si prova solo se `sessionStorage` ha un token, per non
 * sprecare una richiesta a ogni apertura del pannello da anonimo.
 */
export async function restoreSession(): Promise<void> {
  const hasCandidate = config.refreshTransport === 'cookie' || readStoredRefresh() !== null
  if (!hasCandidate) {
    emit({ status: 'anonymous' })
    return
  }

  try {
    const token = await refreshSession()
    if (!token) emit({ status: 'anonymous' })
  } catch {
    // Backend irraggiungibile all'avvio: si parte da anonimo. Il token conservato non
    // viene cancellato, così un tentativo successivo può ancora riuscire.
    emit({ status: 'anonymous' })
  }
}

// ── fine della sessione ───────────────────────────────────────────────────────

/**
 * Perché la sessione è finita. Le due strade devono fare le stesse cose — svuotare la
 * cache, uscire dalla pagina autenticata — e differire solo nel messaggio.
 */
export type SessionEndReason =
  /** L'operatore ha premuto «Esci». */
  | { kind: 'logout' }
  /** Refresh scaduto, riusato, o account disabilitato: non c'è modo di recuperare. */
  | { kind: 'lost'; error: ApiError }

let onSessionEndedHook: ((reason: SessionEndReason) => void) | null = null

/** Registra la reazione alla fine della sessione (svuotare la cache, tornare al login). */
export function setOnSessionEnded(handler: (reason: SessionEndReason) => void): void {
  onSessionEndedHook = handler
}

configureSessionBridge({
  getAccessToken,
  refresh: refreshSession,
  onSessionLost: (error) => {
    clearSession()
    onSessionEndedHook?.({ kind: 'lost', error })
  },
})
